import { FormEventHandler, useEffect, useRef, useState, FC } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Fuse from "fuse.js";
import he from "he";
import Pager from "./Pager";
import { ISearchResultPlus } from "./models";
import { useAllItemsForSearch } from "./utils";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Helper function to convert a number to a Roman numeral.
export function romanize(num: number) {
  const lookup: { [key: string]: number } = {
    M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1,
  };
  let roman = "";
  for (let i in lookup) {
    while (num >= lookup[i]) {
      roman += i;
      num -= lookup[i];
    }
  }
  return roman;
}

const replaceWithRomanNumerals = (s: string) => {
  return s.replace(/((experience|wave|onda)s?) ([1-9][0-9]*)/, (_1, _2, m3, m4) => {
    return `${m3} ${romanize(Number(m4)).toLowerCase()}`;
  });
};

// A reusable Search Icon component
const SearchIcon: FC<{ className?: string }> = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);


const queryClient = new QueryClient();

export const SearchAppInner = ({ base = "" }: { base?: string }) => {
  const { data, isLoading } = useAllItemsForSearch();
  const { term } = useParams();
  const searchTermRef = useRef<HTMLInputElement>(null);
  const [debug, setDebug] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const locationParams = new URLSearchParams(location.search);

  useEffect(() => {
    const onKeyPress = (event: KeyboardEvent) => {
      const tagName = document.activeElement?.tagName.toLowerCase();
      if (tagName === "input" || tagName === "textarea") return;

      if (event.key === "?") {
        setDebug((prev) => !prev);
      }
    };

    document.addEventListener("keypress", onKeyPress);
    return () => {
      document.removeEventListener("keypress", onKeyPress);
    };
  }, []);

  const decodedTerm = replaceWithRomanNumerals(
    decodeURIComponent((term || "").replace(/\+/g, "%20"))
      .toLowerCase()
      .trim()
  ).replace(/^(gateway experience) ([ivx]+)/, "$1 wave $2");

  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!searchTermRef.current) return;
    navigate(`${base}/${searchTermRef.current.value}`);
  };

  const allItems: ISearchResultPlus[] = (data || []).map((e) => ({
    ...e,
    titleForSearch: (e.title + " " + e.artist)
      .replace(/®/g, "")
      .replace(/&#8211;/g, "-")
      .toLowerCase()
      .trim(),
  }));

  const fuseObj = new Fuse(allItems, {
    includeScore: true,
    location: 0,
    keys: ["titleForSearch"],
  });

  const results = term ? fuseObj.search(decodedTerm) : [];

  const exactMatch = new RegExp(`^${decodedTerm}$`);
  const exactStarting = new RegExp(`^${decodedTerm}\\b`);
  const exactSomewhere = new RegExp(`\\b${decodedTerm}\\b`);
  results.forEach((el) => {
    if (!el.score) return;
    if (exactMatch.test(el.item.titleForSearch)) el.score -= 3;
    else if (exactStarting.test(el.item.titleForSearch)) el.score -= 2;
    else if (exactSomewhere.test(el.item.titleForSearch)) el.score -= 1;
  });

  results.sort((a, b) => {
    if (!a.score || !b.score) return 0;
    return a.score - b.score;
  });

  const pageCount = Math.ceil(results.length / 10);
  const pageParam = locationParams.get("page");
  const pageNumber = pageParam !== null ? +pageParam : 0;
  
  const titleText = decodedTerm ? `Search Results for: "${decodedTerm}"` : "Search Our Site";
  const paginatedResults = results.slice(pageNumber * 10, (pageNumber + 1) * 10);

  return (
    <div className="bg-white font-sans">
      {/* Page Header */}
      <div className="bg-gray-50 py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 capitalize text-center">
            {titleText}
          </h1>
        </div>
      </div>

      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Search Form */}
        <form onSubmit={onSubmit} className="max-w-2xl mx-auto mb-12">
          {debug && <p className="text-center text-sm text-red-500 mb-2 font-mono">{decodedTerm}</p>}
          <div className="flex relative rounded-md shadow-sm border border-gray-300 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
            <input
              type="text"
              placeholder="Search …"
              className="flex-1 block w-full border-0 rounded-l-md sm:text-sm py-3 px-4 focus:ring-0 text-gray-900 placeholder-gray-400"
              defaultValue={decodedTerm}
              ref={searchTermRef}
              spellCheck={false}
              required
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-4 border-l border-gray-300 text-sm font-medium rounded-r-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <SearchIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>
        </form>
        
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-indigo-600"></div>
            <p className="mt-4 text-gray-500 font-medium">Searching ...</p>
          </div>
        )}

        {/* Results List */}
        {!isLoading && results.length > 0 && (
          <>
            <div className="my-8">
              <Pager pageCount={pageCount} page={pageNumber} terms={[]} />
            </div>

            {/* Search Results */}
            <div className="divide-y divide-gray-200">
              {paginatedResults.map((r) => (
                <div key={r.item.ID} className="py-8 flex flex-col sm:flex-row gap-6">
                  {r.item.thumbnail_url && (
                    <div className="flex-shrink-0 w-32 h-32 sm:w-40 sm:h-40 border border-gray-200 rounded-lg overflow-hidden">
                      <a href={r.item.permalink}>
                        <img
                          src={r.item.thumbnail_url}
                          alt={he.decode(r.item.title)}
                          className="h-full w-full object-cover object-center"
                        />
                      </a>
                    </div>
                  )}
                  <div className="flex-1 flex flex-col">
                    {debug && (
                      <div className="mb-2 p-2 bg-gray-100 rounded text-xs font-mono break-all text-gray-600">
                        {r.item.ID} : {r.score?.toFixed(4)} : {r.item.titleForSearch}
                      </div>
                    )}
                    <h2 className="text-xl font-bold text-gray-900">
                      <a href={r.item.permalink} className="hover:text-indigo-600 transition-colors">
                        {he.decode(r.item.title)}
                      </a>
                    </h2>
                    <p
                      className="mt-2 text-sm text-gray-600 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: r.item.excerpt }}
                    ></p>
                    <div className="flex-grow"></div>
                    <p className="mt-4 text-sm text-gray-500">{r.item.artist}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="my-8">
              <Pager pageCount={pageCount} page={pageNumber} terms={[]} />
            </div>
          </>
        )}

        {/* No Results State */}
        {!isLoading && term && results.length === 0 && (
            <div className="text-center py-16">
                <h2 className="text-xl font-semibold text-gray-800">No results found</h2>
                <p className="mt-2 text-gray-500">
                    We couldn't find anything for "{decodedTerm}". Please try another search.
                </p>
            </div>
        )}

      </main>
    </div>
  );
};

const SearchApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/search/">
        <Routes>
          <Route path="/" element={<SearchAppInner />} />
          <Route path=":term" element={<SearchAppInner />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default SearchApp;